import { TABLE_PAGINATION_HEIGHT } from '@precis-digital/kurama/Table';
import type { ChartCardProps, ChartDataRow, Dimension, Metric } from '../../components/ChartCard/types';
import { sizeToMinCardHeight } from '../../components/ChartCard/constants';
import { DEFAULT_TABLE_PAGE_SIZE } from '../../components/ChartCard/hooks/useChartCardFetchQuery';
import { DEFAULT_CURRENCY_CODE, type CurrencyCodes } from 'shared/constants/currency';
import { MarkdownTableInlineCell } from 'shared/copilot/tables/MarkdownTableInlineCell';
import formatToMoney, { formatToNumber, formatToPercent, withPositiveSign } from 'shared/numberFormat';
import { buildAgentDimension, buildAgentMetric, humanizeFieldLabel, isTemporalFieldValue } from '../charts/inferDataDefinition';
import { slugifyTableColumnKey } from './parseMarkdownTable';
import { inferColumnNumericStyle, parseMarkdownNumericCell, type MarkdownNumericCellStyle } from './parseMarkdownTableNumericCell';
import { type CopilotTableData } from './types';
import { stripInlineMarkdown } from 'shared/markdown/renderInlineMarkdown';

/** Sticky table head row (matches ChartCard head cell minHeight). */
const TABLE_HEADER_HEIGHT = 48;
/** Sticky totals footer when metrics are present. */
const TABLE_FOOTER_HEIGHT = 48;
const TABLE_ROW_HEIGHT = 40;
const TABLE_VERTICAL_PADDING = 16;
/** Floor for tiny tables — still room for header + a couple of rows. */
const TABLE_MIN_HEIGHT = 220;
/**
 * Cap aligned above ChartCard `full` (538) so chat tables can show ~10–12 body rows
 * even with sticky head/footer/pagination chrome (reporting cards use grid `cardSize`).
 */
const TABLE_MAX_HEIGHT = sizeToMinCardHeight.full + 120;
/** Always reserve viewport for at least this many body rows when the dataset is larger. */
const MIN_VISIBLE_BODY_ROWS = 8;
/** Local pagination kicks in above one default ChartCard page. */
const PAGINATION_ROW_THRESHOLD = DEFAULT_TABLE_PAGE_SIZE;
const columnLooksTemporal = (values: string[]): boolean => values.every(value => {
  const trimmed = stripInlineMarkdown(value).trim();
  return trimmed.length === 0 || isTemporalFieldValue(trimmed);
});
const inferColumnKind = (values: string[]): 'date-dimension' | 'string-dimension' | 'metric' => {
  if (columnLooksTemporal(values)) {
    return 'date-dimension';
  }
  if (inferColumnNumericStyle(values) != null) {
    return 'metric';
  }
  return 'string-dimension';
};

/** Comparison columns: compact shared formatters + '+' for gains (see `withPositiveSign`). */
const formatNullableSignedMetric = (formatValue: (value: number) => string) => (_id: string, value: number | null): string => {
  if (value == null || !Number.isFinite(value)) {
    return 'N/A';
  }
  return withPositiveSign(formatValue(value), value);
};
const formatSignedNumber = formatNullableSignedMetric(value => formatToNumber(value));
const formatSignedPercent = formatNullableSignedMetric(value => formatToPercent(value, 2));
const createSignedCurrencyFormatter = (currencyCode: CurrencyCodes): ReturnType<typeof formatNullableSignedMetric> => formatNullableSignedMetric(value => formatToMoney({
  value,
  currencyCode,
  compact: true
}));
const buildMetricForNumericStyle = ({
  key,
  label,
  values,
  style,
  currencyCode
}: {
  key: string;
  label: string;
  values: string[];
  style: MarkdownNumericCellStyle;
  currencyCode: CurrencyCodes;
}): Metric<string, ChartDataRow<string>> => {
  if (style === 'percentage_comparison') {
    return {
      id: key,
      label,
      type: 'percentage',
      aggregation: 'average',
      format: formatSignedPercent
    };
  }
  if (style === 'percentage') {
    return {
      id: key,
      label,
      type: 'percentage',
      aggregation: 'average'
    };
  }
  const sampleRows = values.map(value => ({
    [key]: parseMarkdownNumericCell(value)
  }));
  const metric = buildAgentMetric(key, sampleRows, {
    currencyCode
  });
  if (style === 'absolute_comparison') {
    if (metric.type === 'currency') {
      return {
        ...metric,
        label,
        currencyCode,
        format: createSignedCurrencyFormatter(currencyCode)
      };
    }
    return {
      id: key,
      label,
      type: 'number',
      aggregation: 'sum',
      format: formatSignedNumber
    };
  }
  if (metric.type === 'currency') {
    return {
      ...metric,
      label,
      currencyCode
    };
  }
  return {
    ...metric,
    label
  };
};
export const canMapMarkdownTableToChartCard = (table: CopilotTableData): boolean => table.headers.length >= 2 && table.rows.length > 0;
export interface MapMarkdownTableToChartCardOptions {
  currencyCode?: CurrencyCodes;
}
export const mapMarkdownTableToChartCardProps = (table: CopilotTableData, options?: MapMarkdownTableToChartCardOptions): ChartCardProps<string, ChartDataRow<string>> | null => {
  if (!canMapMarkdownTableToChartCard(table)) {
    return null;
  }
  const currencyCode = options?.currencyCode ?? DEFAULT_CURRENCY_CODE;
  const columnKeys = table.headers.map((header, index) => slugifyTableColumnKey(stripInlineMarkdown(header), index));
  const columnValues = columnKeys.map((_, columnIndex) => table.rows.map(row => row[columnIndex] ?? ''));
  const columnKinds = columnValues.map(values => inferColumnKind(values));
  const columnNumericStyles = columnValues.map((values, columnIndex) => columnKinds[columnIndex] === 'metric' ? inferColumnNumericStyle(values) ?? 'number' : null);
  const dimensions: Array<Dimension<'props', string, ChartDataRow<string>>> = [];
  const metrics: Array<Metric<string, ChartDataRow<string>>> = [];
  for (let columnIndex = 0; columnIndex < table.headers.length; columnIndex++) {
    const header = table.headers[columnIndex];
    const key = columnKeys[columnIndex];
    const values = columnValues[columnIndex];
    const kind = columnKinds[columnIndex];
    const label = humanizeFieldLabel(stripInlineMarkdown(header));
    if (kind === 'date-dimension') {
      dimensions.push({
        id: key,
        label,
        type: 'date',
        format: 'MMM d, yyyy'
      });
      continue;
    }
    if (kind === 'metric') {
      metrics.push(buildMetricForNumericStyle({
        key,
        label,
        values,
        style: columnNumericStyles[columnIndex] ?? 'number',
        currencyCode
      }));
      continue;
    }
    dimensions.push({
      ...buildAgentDimension(key, values.map(value => ({
        [key]: value
      }))),
      label
    });
  }
  const data: Array<ChartDataRow<string>> = table.rows.map(row => {
    const normalizedRow: ChartDataRow<string> = {};
    for (let columnIndex = 0; columnIndex < columnKeys.length; columnIndex++) {
      const key = columnKeys[columnIndex];
      const rawValue = row[columnIndex] ?? '';
      const kind = columnKinds[columnIndex];
      if (kind === 'metric') {
        normalizedRow[key] = parseMarkdownNumericCell(rawValue);
      } else if (kind === 'date-dimension') {
        normalizedRow[key] = stripInlineMarkdown(rawValue).trim();
      } else {
        normalizedRow[key] = rawValue;
      }
    }
    return normalizedRow;
  });
  const usePagination = data.length > PAGINATION_ROW_THRESHOLD;
  const footerHeight = metrics.length > 0 ? TABLE_FOOTER_HEIGHT : 0;
  const paginationHeight = usePagination ? TABLE_PAGINATION_HEIGHT : 0;
  const chromeHeight = TABLE_HEADER_HEIGHT + footerHeight + paginationHeight + TABLE_VERTICAL_PADDING;
  const visibleRowCount = usePagination ? Math.min(data.length, DEFAULT_TABLE_PAGE_SIZE) : data.length;
  const contentHeight = chromeHeight + visibleRowCount * TABLE_ROW_HEIGHT;
  const minViewportForReadableBody = chromeHeight + MIN_VISIBLE_BODY_ROWS * TABLE_ROW_HEIGHT;
  const unconstrainedHeight = data.length > MIN_VISIBLE_BODY_ROWS ? Math.max(TABLE_MIN_HEIGHT, contentHeight, minViewportForReadableBody) : Math.max(TABLE_MIN_HEIGHT, contentHeight);
  const tableHeight = Math.min(TABLE_MAX_HEIGHT, unconstrainedHeight);
  return {
    card: {
      id: table.sourceTableId,
      title: 'Results',
      type: 'table',
      columns: columnKeys.map((key, columnIndex) => {
        if (columnKinds[columnIndex] !== 'string-dimension') {
          return key;
        }
        return {
          id: key,
          accessorKey: key,
          cell: context => {
            const value = context.getValue();
            return <MarkdownTableInlineCell value={value == null ? '' : String(value)} />;
          }
        };
      })
    },
    dataDefinition: {
      dimensions,
      metrics
    },
    data: {
      data,
      isLoading: false,
      isError: false
    },
    mutable: {
      selections: {}
    },
    customisations: {
      allowChangeCardType: false,
      allowExport: true,
      useDefaultSorting: false,
      useDefaultPinning: false,
      useBodyScrollDecoration: true,
      allowColumnResizing: true,
      allowColumnPinning: true,
      usePagination,
      tableSize: {
        height: tableHeight
      }
    }
  };
};